import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Upload, X, Image, Crop } from "lucide-react";
import { ImageCropper } from "./ImageCropper";

interface TradesmanLogoUploadProps {
  currentLogoUrl?: string | null;
  onLogoUpdate: (logoUrl: string | null) => void;
}

export function TradesmanLogoUpload({ currentLogoUrl, onLogoUpdate }: TradesmanLogoUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(currentLogoUrl || null);
  const [showCropDialog, setShowCropDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please upload a valid image file (JPG, PNG, or WebP)");
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB");
      return;
    }

    setSelectedFile(file);
    
    // Create preview URL for cropping
    const reader = new FileReader();
    reader.onload = (e) => {
      setImageSrc(e.target?.result as string);
      setShowCropDialog(true);
    };
    reader.readAsDataURL(file);
  };

  const handleCroppedUpload = async (blob: Blob) => {
    if (!selectedFile) return;

    setIsUploading(true);
    setShowCropDialog(false);

    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("You must be logged in to upload a logo");
        return;
      }

      // Create file path
      const fileExt = selectedFile.name.split('.').pop() || 'jpg';
      const filePath = `${user.id}/${Date.now()}-logo.${fileExt}`;

      // Delete existing logo if present
      if (currentLogoUrl) {
        const oldPath = currentLogoUrl.split('/').slice(-2).join('/');
        await supabase.storage.from('tradesman-logos').remove([oldPath]);
      }

      // Upload cropped logo
      const { error: uploadError } = await supabase.storage
        .from('tradesman-logos')
        .upload(filePath, blob, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        console.error('Upload error:', uploadError);
        throw uploadError;
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('tradesman-logos')
        .getPublicUrl(filePath);

      // Update profile with new logo URL
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ logo_url: publicUrl })
        .eq('user_id', user.id);

      if (updateError) {
        console.error('Profile update error:', updateError);
        throw updateError;
      }

      setPreviewUrl(publicUrl);
      onLogoUpdate(publicUrl);
      toast.success("Logo uploaded and cropped successfully!");

    } catch (error: any) {
      console.error('Error uploading logo:', error);
      toast.error(error.message || "Failed to upload logo");
    } finally {
      setIsUploading(false);
      setSelectedFile(null);
      setImageSrc(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleCancelCrop = () => {
    setShowCropDialog(false);
    setSelectedFile(null);
    setImageSrc(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveLogo = async () => {
    if (!currentLogoUrl) return;

    setIsUploading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("You must be logged in to remove logo");
        return;
      }

      // Remove from storage
      const oldPath = currentLogoUrl.split('/').slice(-2).join('/');
      await supabase.storage.from('tradesman-logos').remove([oldPath]);

      // Update profile
      const { error } = await supabase
        .from('profiles')
        .update({ logo_url: null })
        .eq('user_id', user.id);

      if (error) throw error;

      setPreviewUrl(null);
      onLogoUpdate(null);
      toast.success("Logo removed successfully!");

    } catch (error: any) {
      console.error('Error removing logo:', error);
      toast.error("Failed to remove logo");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Image className="h-5 w-5" />
          Company Logo
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {previewUrl ? (
          <div className="flex flex-col items-center space-y-4">
            <div className="relative">
              <img
                src={previewUrl}
                alt="Company logo"
                className="h-24 w-24 object-contain border border-border rounded-lg bg-background p-2"
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
              >
                <Upload className="h-4 w-4 mr-2" />
                Replace Logo
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRemoveLogo}
                disabled={isUploading}
              >
                <X className="h-4 w-4 mr-2" />
                Remove
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center space-y-4">
            <div className="h-24 w-24 border-2 border-dashed border-border rounded-lg flex items-center justify-center bg-muted/50">
              <Image className="h-8 w-8 text-muted-foreground" />
            </div>
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
            >
              <Upload className="h-4 w-4 mr-2" />
              Upload Logo
            </Button>
          </div>
        )}

        <Input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={handleFileSelect}
          className="hidden"
        />

        <div className="text-sm text-muted-foreground space-y-1">
          <p>• Supported formats: JPG, PNG, WebP</p>
          <p>• Maximum file size: 5MB</p>
          <p>• You can crop and adjust the image after selection</p>
        </div>
      </CardContent>

      <Dialog open={showCropDialog} onOpenChange={setShowCropDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crop className="h-5 w-5" />
              Crop Your Logo
            </DialogTitle>
          </DialogHeader>
          
          {imageSrc && (
            <ImageCropper
              src={imageSrc}
              aspect={1}
              onCancel={handleCancelCrop}
              onConfirm={handleCroppedUpload}
            />
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}